/**
 * Stage 7 — the ONE customer-support boundary for web.space.
 *
 * Requirement 11: a Mindspace-level `customersupportenabled` setting gates
 * every customer-support surface. The point of this module is that the policy
 * lives in exactly one place: a future Help button, Contact-us link or support
 * mailto CANNOT be added without going through `useCustomerSupport`, because
 * `customer-support.contract.spec.ts` fails the build if it tries.
 *
 * The policy itself lives in the import-free `customer-support-policy.ts` so
 * `hubspot-initializer.tsx` can consume it without a module cycle; the failure
 * direction is documented there.
 *
 * Surfaces with genuinely no Mindspace — identity, the global `web.app`, the
 * browser extension, global emails — are not served by this hook at all and
 * stay support-enabled by construction.
 *
 * ── Stage 1 wiring point ──────────────────────────────────────────────────
 * Stage 1 adds `customerSupportEnabled` to `Mindspace.modules` and selects it
 * in root.tsx's `SpaceMetadata` fragment. Until that lands the field is simply
 * absent from the carrier, which normalizes to the capability default (true) —
 * the same direction this seam must fail in anyway. `customer-support.spec.tsx`
 * carries a handshake test that FAILS once the generated client type has the
 * field but the root query has not been updated to select it, so the wiring
 * cannot be half-done silently.
 */
import { useCallback, useMemo } from "react";
import { useHubSpot } from "~/components/hubspot-initializer";
import { useCurrentSpace } from "~/hooks/context-hook";
import {
  CUSTOMER_SUPPORT_EMAIL,
  resolveCustomerSupport,
} from "~/hooks/customer-support-policy";

export {
  CUSTOMER_SUPPORT_EMAIL,
  resolveCustomerSupport,
} from "~/hooks/customer-support-policy";
export type { CustomerSupportCarrier } from "~/hooks/customer-support-policy";

/**
 * What every support-bearing surface consumes. Deliberately not a bare
 * boolean: the callable `open` and the pre-built `mailtoHref` mean a call site
 * never needs `useHubSpot` or a hard-coded address of its own, which is what
 * makes the static contract enforceable.
 */
export type SupportAction = Readonly<{
  /** True when actionable customer-support affordances may be shown. */
  available: boolean;
  /** Opens the support chat. A no-op when support is unavailable. */
  open: () => void;
  /** The support address, or `undefined` when support is unavailable. */
  address: string | undefined;
  /** `mailto:` href for the support inbox, or `undefined` when unavailable. */
  mailtoHref: string | undefined;
}>;

/**
 * The canonical support seam. Every visible chat, help and contact control in
 * web.space goes through this.
 */
export function useCustomerSupport(): SupportAction {
  const space = useCurrentSpace();
  const { open: openHubSpot } = useHubSpot();
  const available = resolveCustomerSupport(space);

  const open = useCallback(() => {
    if (!available) return;
    openHubSpot();
  }, [available, openHubSpot]);

  return useMemo(
    () => ({
      available,
      open,
      address: available ? CUSTOMER_SUPPORT_EMAIL : undefined,
      mailtoHref: available ? `mailto:${CUSTOMER_SUPPORT_EMAIL}` : undefined,
    }),
    [available, open],
  );
}
